import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  advancePathologyStatus,
  fetchNonPathologyWorklist,
  fetchPathologyWorklist,
  fetchPathologyReports,
  fetchRadiologyWorklist,
  fetchReadyReportWorklist,
  updatePathologyReports,
} from './pathology.api';
import type { PathologyAction, PathologyListFilters, UpdatePathologyReportsPayload, WorklistFilters } from './types';

export const usePathologyWorklist = (filters: PathologyListFilters) => useQuery({
  queryKey: ['pathology', 'worklist', filters],
  queryFn: () => fetchPathologyWorklist(filters),
});

export const useNonPathologyWorklist = (filters: WorklistFilters) => useQuery({
  queryKey: ['pathology', 'non-pathology', filters],
  queryFn: () => fetchNonPathologyWorklist(filters),
});

export const useRadiologyWorklist = (filters: WorklistFilters) => useQuery({
  queryKey: ['pathology', 'radiology', filters],
  queryFn: () => fetchRadiologyWorklist(filters),
});

export const useReadyReportWorklist = (filters: WorklistFilters) => useQuery({
  queryKey: ['pathology', 'ready-report', filters],
  queryFn: () => fetchReadyReportWorklist(filters),
});

export const usePathologyReports = (billId: string | undefined, lineIds: string[]) => useQuery({
  queryKey: ['pathology', 'reports', billId, lineIds],
  queryFn: () => fetchPathologyReports(billId!, lineIds),
  enabled: Boolean(billId) && lineIds.length > 0,
});

export const useAdvancePathologyStatus = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, lineIds, action }: { billId: string; lineIds: string[]; action: PathologyAction }) => advancePathologyStatus(billId, lineIds, action),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['pathology'] }),
  });
};

export const useUpdatePathologyReports = (billId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePathologyReportsPayload) => updatePathologyReports(billId, payload),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['pathology'] }),
        client.invalidateQueries({ queryKey: ['pathology', 'reports', billId] }),
      ]);
    },
  });
};
